import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, PaperPlaneTilt, EnvelopeSimple } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../i18n/translations';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', service: '', budget: '', timeline: '', message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ type: 'success', message: t.contact.form.success });
      setFormData({ name: '', email: '', phone: '', company: '', service: '', budget: '', timeline: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: t.contact.form.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Phone, label: t.contact.info.phone, values: companyInfo.phones },
    { icon: EnvelopeSimple, label: t.contact.info.email, values: [companyInfo.email, companyInfo.emailTeam] },
    { icon: MapPin, label: t.contact.info.address, values: [t.contact.info.address_full] },
    { icon: Clock, label: t.contact.info.hours, values: [t.contact.info.hours_value] },
  ];

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-16">
          <p className="text-[#FF6B00] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4">Contact</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1320] tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="contact-title">{t.contact.title}</h2>
          <p className="text-base text-[#475569] mt-4 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>{t.contact.subtitle}</p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Info */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
            <div className="space-y-8 mb-10">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-14 h-14 bg-[#0B1320] flex-shrink-0 flex items-center justify-center">
                    <info.icon size={24} className="text-[#FF6B00]" weight="bold" />
                  </div>
                  <div>
                    <p className="text-xs text-[#475569] uppercase tracking-wider mb-1">{info.label}</p>
                    {info.values.map((value, i) => (
                      <p key={i} className="text-base font-medium text-[#0B1320] whitespace-pre-line">{value}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="aspect-video overflow-hidden border border-black/10">
              <iframe
                title="JKM Engineering Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63663.50697390248!2d22.383333!3d-5.883333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1a54a6e10c7b04e7%3A0x4b6e0c24c9f70c8e!2sKananga%2C%20Democratic%20Republic%20of%20the%20Congo!5e0!3m2!1sfr!2scd!4v1706000000000!5m2!1sfr!2scd"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Form */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: true }} className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-xs font-medium text-[#0B1320] uppercase tracking-wider">{t.contact.form.name}</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="mt-2 rounded-none border-black/20 focus:border-[#FF6B00] focus:ring-[#FF6B00] h-12" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-medium text-[#0B1320] uppercase tracking-wider">{t.contact.form.email}</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-2 rounded-none border-black/20 focus:border-[#FF6B00] focus:ring-[#FF6B00] h-12" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="phone" className="text-xs font-medium text-[#0B1320] uppercase tracking-wider">{t.contact.form.phone}</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="mt-2 rounded-none border-black/20 focus:border-[#FF6B00] focus:ring-[#FF6B00] h-12" />
                </div>
                <div>
                  <Label htmlFor="company" className="text-xs font-medium text-[#0B1320] uppercase tracking-wider">{t.contact.form.company}</Label>
                  <Input id="company" name="company" value={formData.company} onChange={handleChange} className="mt-2 rounded-none border-black/20 focus:border-[#FF6B00] focus:ring-[#FF6B00] h-12" />
                </div>
              </div>

              <div>
                <Label htmlFor="service" className="text-xs font-medium text-[#0B1320] uppercase tracking-wider">{t.contact.form.service}</Label>
                <select id="service" name="service" value={formData.service} onChange={handleChange} className="mt-2 w-full h-12 border border-black/20 bg-white px-3 text-sm focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00] outline-none">
                  <option value="">{t.contact.form.service_options.placeholder}</option>
                  <option value="civil">{t.contact.form.service_options.civil}</option>
                  <option value="electrical">{t.contact.form.service_options.electrical}</option>
                  <option value="topography">{t.contact.form.service_options.topography}</option>
                  <option value="drilling">{t.contact.form.service_options.drilling}</option>
                  <option value="construction">{t.contact.form.service_options.construction}</option>
                  <option value="consulting">{t.contact.form.service_options.consulting}</option>
                </select>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="budget" className="text-xs font-medium text-[#0B1320] uppercase tracking-wider">{t.contact.form.budget}</Label>
                  <select id="budget" name="budget" value={formData.budget} onChange={handleChange} className="mt-2 w-full h-12 border border-black/20 bg-white px-3 text-sm focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00] outline-none">
                    <option value="">{t.contact.form.budget_options.placeholder}</option>
                    <option value="small">{t.contact.form.budget_options.small}</option>
                    <option value="medium">{t.contact.form.budget_options.medium}</option>
                    <option value="large">{t.contact.form.budget_options.large}</option>
                    <option value="enterprise">{t.contact.form.budget_options.enterprise}</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="timeline" className="text-xs font-medium text-[#0B1320] uppercase tracking-wider">{t.contact.form.timeline}</Label>
                  <select id="timeline" name="timeline" value={formData.timeline} onChange={handleChange} className="mt-2 w-full h-12 border border-black/20 bg-white px-3 text-sm focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00] outline-none">
                    <option value="">{t.contact.form.timeline_options.placeholder}</option>
                    <option value="urgent">{t.contact.form.timeline_options.urgent}</option>
                    <option value="short">{t.contact.form.timeline_options.short}</option>
                    <option value="medium">{t.contact.form.timeline_options.medium}</option>
                    <option value="long">{t.contact.form.timeline_options.long}</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-xs font-medium text-[#0B1320] uppercase tracking-wider">{t.contact.form.message}</Label>
                <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="mt-2 rounded-none border-black/20 focus:border-[#FF6B00] focus:ring-[#FF6B00] resize-none" />
              </div>

              {status.message && (
                <div className={`p-4 text-sm ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {status.message}
                </div>
              )}

              <Button type="submit" disabled={isSubmitting} className="w-full bg-[#FF6B00] hover:bg-[#FF6B00]/90 text-white rounded-none h-14 text-base font-medium tracking-wide">
                {isSubmitting ? t.contact.form.sending : (<>{t.contact.form.submit}<PaperPlaneTilt size={20} className="ml-2" weight="bold" /></>)}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
